import { useReveal } from "../../../../hooks/useReveal";
import styles from "./CoreFeatures.module.css";

const features = [
  {
    title: "AI-Powered Insights",
    body: "Understand candidate strengths, weaknesses and potential instantly with AI-generated insights based on assessments and profiles.",
    meta: "See insights in action",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    title: "Anti-Faking Measures",
    body: "Built-in validity checks and consistency scoring catch impression management, so results reflect who candidates truly are — not who they want you to see.",
    meta: "See how we catch faking",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
  },
  {
    title: "Smart Matching",
    body: "Get matched with candidates that fit your role based on skills, behavior and potential — not just keyword overlap.",
    meta: "How matching works",
    icon: (
      <svg viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <path d="M11 8v3l2 2" />
      </svg>
    ),
  },
  {
    title: "Psychometric Assessments",
    body: "Evaluate candidates using structured tests that reveal real capabilities, work traits and how they think under pressure.",
    meta: "Explore assessments",
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
  {
    title: "Talent Profiles",
    body: "View structured profiles that go beyond resumes — skills, insights and performance indicators all in one clean view.",
    meta: "View a sample profile",
    icon: (
      <svg viewBox="0 0 24 24">
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="9" y1="21" x2="9" y2="9" />
      </svg>
    ),
  },
  {
    title: "Faster Hiring Workflow",
    body: "Reduce screening time and make decisions quicker with organized candidate data, side-by-side comparisons and shareable insights.",
    meta: "See the workflow",
    icon: (
      <svg viewBox="0 0 24 24">
        <polyline points="13 17 18 12 13 7" />
        <polyline points="6 17 11 12 6 7" />
      </svg>
    ),
  },
];

function FeatureCard({ title, body, meta, icon }) {
  const { ref, isVisible } = useReveal();
  return (
    <article
      ref={ref}
      className={`${styles.card} ${isVisible ? styles.visible : styles.hidden}`}
    >
      <div className={styles.icon}>{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
      {/* <span className={styles.meta}>{meta} </span> */}
    </article>
  );
}

export default function CoreFeatures() {
  return (
    <section className={styles.section} id="features">
      <div className={styles.container}>
        <div className={styles.sectionHead}>
          <span className={styles.eyebrow}>Core features</span>
          <h2 className={styles.h2}>Built to surface real talent fast.</h2>
          <p className={styles.sub}>
            Six tools that work together to give you a complete picture of every
            candidate, automatically.
          </p>
        </div>

        <div className={styles.grid}>
          {features.map((f) => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </div>
      <div className={styles.videoWrapper}>
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/LkeR-OCRw2Q?si=rzmgw2fnKhubITPr&rel=0&modestbranding=1&showinfo=0&loop=1"
          title="Awasource Talent Walkthrough Video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </section>
  );
}
